import { useNavigate } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { StatusVariant } from '@factorialco/f0-react'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add } from '@factorialco/f0-react/icons/app'
import {
  AtsHiringPlanRequisitionsListDocument,
  AtsHiringPlanRequisitionsLocationsDocument,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToRelativeTime } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

const requisitionStatusToColor: Record<string, StatusVariant> = {
  pending: 'warning',
  approved: 'positive',
  cancelled: 'neutral',
  rejected: 'critical',
}

const requisitionStatusLabel = (status: string) => {
  return {
    pending: i18n.t('ats.vacancies_requisition_page.status.pending'),
    approved: i18n.t('ats.vacancies_requisition_page.status.approved'),
    cancelled: i18n.t('ats.vacancies_requisition_page.status.cancelled'),
    rejected: i18n.t('ats.vacancies_requisition_page.status.rejected'),
  }[status]
}

export const VacanciesRequisitionsTable = () => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const client = useApolloClient()

  const source = useGraphqlDataCollectionSource({
    document: AtsHiringPlanRequisitionsListDocument,
    mapData: (data) => data.ats.requisitionsConnection.nodes,
    mapPagination: (data) => ({
      total: data.ats.requisitionsConnection.totalCount ?? 0,
    }),
    mapVariables: ({ sortings, filters, search }) => {
      const status = filters?.status?.map(String).filter(Boolean)
      const workplaceIds = filters?.workplaceIds?.map(String).filter(Boolean) || undefined

      return {
        sort: sortings?.[0],
        status: status?.length ? status : undefined,
        workplaceIds,
        search,
      }
    },
    search: {
      enabled: true,
    },
    sortings: {
      jobPostingTitle: {
        label: i18n.t('ats.vacancies_requisition_page.table_headers.vacancy_name'),
        sortBy: 'jobPostingTitle',
      },
      createdAt: {
        label: i18n.t('ats.vacancies_requisition_page.table_headers.requested_on'),
        sortBy: 'createdAt',
      },
      status: {
        label: i18n.t('ats.vacancies_requisition_page.table_headers.status'),
        sortBy: 'status',
      },
    },
    defaultSortings: {
      field: 'createdAt',
      order: 'desc',
    },
    filters: {
      status: {
        label: i18n.t('ats.vacancies_requisition_page.table_headers.status'),
        type: 'in',
        options: {
          options: [
            {
              label: i18n.t('ats.vacancies_requisition_page.status.pending'),
              value: 'pending',
            },
            {
              label: i18n.t('ats.vacancies_requisition_page.status.approved'),
              value: 'approved',
            },
            {
              label: i18n.t('ats.vacancies_requisition_page.status.cancelled'),
              value: 'cancelled',
            },
            {
              label: i18n.t('ats.vacancies_requisition_page.status.rejected'),
              value: 'rejected',
            },
          ],
        },
      },
      workplaceIds: {
        label: i18n.t('ats.vacancies_requisition_page.table_headers.location'),
        type: 'in',
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: AtsHiringPlanRequisitionsLocationsDocument,
              fetchPolicy: 'cache-first',
            })

            return (result.data?.locations?.locationsConnection?.nodes ?? []).map((location) => ({
              label: location.name || '',
              value: String(location.id),
            }))
          },
        },
      },
    },
    presets: [
      {
        label: i18n.t('ats.vacancies_requisition_page.status.pending'),
        filter: {
          status: ['pending'],
        },
      },
      {
        label: i18n.t('ats.vacancies_requisition_page.status.approved'),
        filter: {
          status: ['approved'],
        },
      },
      {
        label: i18n.t('ats.vacancies_requisition_page.status.cancelled'),
        filter: {
          status: ['cancelled'],
        },
      },
      {
        label: i18n.t('ats.vacancies_requisition_page.status.rejected'),
        filter: {
          status: ['rejected'],
        },
      },
    ],
    primaryActions: () => ({
      label: i18n.t('ats.vacancies_requisition_page.primary_action'),
      icon: Add,
      onClick: () => navigate(navigation.ats.hiringPlan.requisitions.new.fullPath()),
    }),
    itemUrl: ({ id }) =>
      navigation.ats.hiringPlan.requisitions.requisitionDetail.fullPath({
        requisition_id: id,
      }),
  })

  return (
    <OneDataCollection
      source={source}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: [
              {
                label: i18n.t('ats.vacancies_requisition_page.table_headers.vacancy_name'),
                render: ({ jobPostingTitle }) => jobPostingTitle,
                sorting: 'jobPostingTitle',
              },
              {
                label: i18n.t('ats.vacancies_requisition_page.table_headers.location'),
                render: ({ workplace }) => workplace?.name ?? '',
              },
              {
                label: i18n.t('ats.vacancies_requisition_page.table_headers.number_of_vacancies'),
                render: ({ numberOfPositions }) => numberOfPositions ?? '-',
              },
              {
                label: i18n.t('ats.vacancies_requisition_page.table_headers.line_manager'),
                render: ({ lineManager }) => {
                  if (!lineManager) return '-'
                  return {
                    type: 'person',
                    value: {
                      firstName: lineManager.firstName ?? '',
                      lastName: lineManager.lastName ?? '',
                      src: buildUrl(lineManager.avatar?.url ?? ''),
                    },
                  }
                },
              },
              {
                label: i18n.t('ats.vacancies_requisition_page.table_headers.requested_on'),
                render: ({ createdAt }) => dateBackendDateTimeToRelativeTime(createdAt),
                sorting: 'createdAt',
              },
              {
                label: i18n.t('ats.vacancies_requisition_page.table_headers.status'),
                render: ({ status }) => ({
                  type: 'status',
                  value: {
                    status: requisitionStatusToColor[status] ?? 'neutral',
                    label:
                      requisitionStatusLabel(status) ??
                      i18n.t('ats.vacancies_requisition_page.status.undefined'),
                  },
                }),
                sorting: 'status',
              },
            ],
          },
        },
      ]}
    />
  )
}
